<template>
  <BasicCard type="default">
    <template #header>
      <PageIcon name="message">Customer Acknowledgment</PageIcon>
    </template>

    <template v-if="!loading && !error && !isEditable && userCanEdit" #actions>
      <EditButton @click="toggleEdit">Edit</EditButton>
    </template>

    <EmptyState
      v-if="error"
      icon="message"
      :subtitle="error.message"
      title="Unable to load Customer Acknowledgment settings."
    />

    <template v-else>
      <div class="text-typography-primary font-semibold text-sm">Customer Disclaimer</div>
      <p class="text-typography-disabled text-xs mt-1 mb-4">
        Shown to the customer just before they sign and submit a claim.
      </p>

      <StatusBadge :type="isStandard ? 'neutral' : 'primary'" :low-contrast="true" class="mb-2">
        {{ isStandard ? 'Standard text' : 'Custom text' }}
      </StatusBadge>

      <PulseTextArea
        v-if="isEditable"
        id="customer_disclaimer"
        v-model="draft"
        class="ack-textarea"
        autocomplete="nope"
      />
      <div
        v-else
        class="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-4 py-3 text-sm leading-relaxed text-typography-disabled whitespace-pre-wrap"
      >
        {{ displayValue }}
      </div>

      <p v-if="isStandard" class="mt-3 flex items-start gap-2 text-xs text-typography-secondary">
        <LineGlobe class="h-4 w-4 shrink-0 fill-path-current text-typography-disabled" />
        Concierge translates this disclaimer into each shopper's language, matching the rest of the widget.
      </p>
      <div v-else class="mt-3 flex gap-3 rounded-lg border border-warning-700 bg-warning-100 p-4">
        <IconWarning class="h-5 w-5 shrink-0 fill-path-current text-warning-800" />
        <div>
          <div class="text-sm font-semibold text-warning-800">This text is not translated</div>
          <p class="mt-1 text-xs leading-relaxed text-warning-900">
            Shoppers viewing the widget in another language will see this text exactly as written. Reset to the standard
            text to let Concierge translate it.
          </p>
          <PulseButton v-if="isEditable" variant="secondary" class="mt-3" @click="resetToStandard">
            Reset to standard text
          </PulseButton>
        </div>
      </div>
    </template>

    <template v-if="isEditable" #footer>
      <div class="flex items-center justify-end w-full gap-2 border-t border-neutral-300 py-4 px-8">
        <PulseButton variant="secondary" @click="onCancel">Cancel</PulseButton>
        <PulseButton class="w-16" @click="onSave">Save</PulseButton>
      </div>
    </template>
  </BasicCard>
</template>

<script lang="ts" setup>
import { isStandardDisclaimer, STANDARD_CUSTOMER_DISCLAIMER } from '~/support/constants/customer-disclaimer';

const props = defineProps({
  data: {
    default: () => ({ customerDisclaimer: '' }),
    type: Object as PropType<{ customerDisclaimer: string }>,
  },
  error: {
    default: undefined,
    type: Object as PropType<{ message: string } | undefined>,
  },
  loading: {
    default: false,
    type: Boolean,
  },
  userCanEdit: {
    default: true,
    type: Boolean,
  },
});

const emit = defineEmits(['update']);

const isEditable = ref(false);
const draft = ref('');

// While editing we track an isolated draft; in read mode we reflect the saved value.
const displayValue = computed(() => (isEditable.value ? draft.value : props.data.customerDisclaimer));
const isStandard = computed(() => isStandardDisclaimer(displayValue.value));

function toggleEdit() {
  draft.value = props.data.customerDisclaimer;
  isEditable.value = true;
}

function resetToStandard() {
  draft.value = STANDARD_CUSTOMER_DISCLAIMER;
}

function onCancel() {
  isEditable.value = false;
}

function onSave() {
  isEditable.value = false;
  emit('update', draft.value);
}
</script>

<style scoped>
.ack-textarea :deep(textarea) {
  min-height: 140px;
}
</style>
